import * as React from 'react';
import { noop } from 'lodash';
import { makeAvatar, makeAvatarStack, makeUserPicker, RendererNext, useWebRuntime } from '@atlassian/forge-ui/ui';
import type { ComponentMap } from '@atlassian/forge-ui/ui';
import { TOP_LEVEL_COMPONENTS } from '@atlassian/help-center-common-component/forge-ui/constants';
import { getApolloClient } from '@atlassian/help-center-common-util/apollo';
import { getMentionProvider } from '../../mention-provider';
import { TopLevelComponent } from '../top-level-component';
import type { ForgeUIKitRendererProps } from '../types';

export const UIKit1Renderer: React.FC<ForgeUIKitRendererProps> = ({
    extension,
    getContextToken,
    getComponents,
    contextIds,
    requestKey,
    cloudId,
    accountId,
    onSubmit,
    loadingComponent,
    onRender = noop,
    extensionData: extensionDataLocal,
}) => {
    const apolloClient = getApolloClient();
    const [rawDispatch, { forgeDoc, error, loading }] = useWebRuntime({
        apolloClient,
        contextIds,
        getContextToken,
        extensionId: extension?.id,
        coreData: { cloudId, localId: extension.id },
    });
    React.useEffect(() => {
        onRender(forgeDoc);
        if (onSubmit) {
            onSubmit(forgeDoc);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forgeDoc, onRender]);
    const dispatchWithData = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (effect: any) =>
            rawDispatch({
                ...effect,
                // Suppressing existing violation. Please fix this.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                extensionData: {
                    // TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    ...effect.extensionData,
                    ...extensionDataLocal,
                },
            }),
        [rawDispatch, extensionDataLocal]
    );
    React.useEffect(() => {
        // Disabling existing violations, should be fixed when revisited.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        dispatchWithData({ type: 'render' });
    }, [dispatchWithData]);
    const wiredComponents = React.useMemo(
        () => ({
            Avatar: makeAvatar({ client: apolloClient }),
            AvatarStack: makeAvatarStack({ client: apolloClient }),
            ...(accountId && {
                UserPicker: makeUserPicker({
                    accountId,
                    cloudId,
                    client: apolloClient,
                    mentionProvider: Promise.resolve(getMentionProvider(accountId, requestKey)),
                    productKey: 'jira',
                }),
            }),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [extension, requestKey, accountId, cloudId, apolloClient]
    );

    // @ts-ignore TS(7053) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message

    // Suppressing existing violation. Please fix this.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-assignment
    const topLevelComponent: string = React.useMemo(() => TOP_LEVEL_COMPONENTS[extension.type], [extension.type]);

    const componentMap = React.useCallback(
        (defaults: ComponentMap) =>
            getComponents
                ? getComponents({ ...defaults, ...wiredComponents })
                : {
                      ...defaults,
                      ...wiredComponents,
                      ...(topLevelComponent && { [topLevelComponent]: TopLevelComponent }),
                  },
        [getComponents, topLevelComponent, wiredComponents]
    );
    return (
        <RendererNext
            forgeDoc={forgeDoc}
            loading={loading}
            error={error}
            dispatch={dispatchWithData}
            components={componentMap}
            loadingComponent={loadingComponent}
            extension={extension}
            client={apolloClient}
        />
    );
};
